import React, { useEffect, useContext } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import _ from "lodash";
import { Typography, Card } from "@material-ui/core";
import { withRouter, RouteComponentProps } from "react-router";

import routes from "../../constants/routes";
import { Icon } from "../../components/icons/Icon";
import { PageHeader, PageRoot, TextField, Button } from "../../components";
import { Premises } from "../../constants/interfaces";
import DetailCard from "../../components/details/DetailCard";
import { premisesActions } from "../../reducers/premises";
import { PREMISES_AUTHORISATION_CONFIRMATION } from "../../constants/messages";
import { RxForm, FormValues, FormSection } from "../../components/Form";
import CheckboxField from "../../components/Fields/CheckboxField";
import FormTextField from "../../components/Fields/FormTextField";
import { SubmitButton } from "../../components/Form/SubmitButton";
import { ResetButton } from "../../components/Form/ResetButton";
import { OnUnSaveChange } from "../../components/Form/OnUnSaveChange";
import { ErrorMessage } from "../../components/Messages";
import { UIMessageContext } from "../../components/UIMessage";
import { websiteValidator, emailValidator } from "./validator";

type Props = RouteComponentProps & {
  classes?: any;
  error?: Error;
  loading: boolean;
  premise: Premises;
  getPremise: (id: string) => Promise<Premises>;
  updatePremise: (premise: Premises, callback?: () => void) => Promise<void>;
};

const useStyles = makeStyles(theme => ({
  form: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    flexDirection: "column"
  },
  formCard: {
    padding: theme.spacing(4),
    maxWidth: theme.breakpoints.values.md
  },
  header: {
    alignItems: "center",
    display: "flex"
  },
  icon: {
    color: theme.palette.primary.light,
    marginRight: theme.spacing(2)
  },
  contentWrapper: {
    display: "flex",
    flexFlow: "row wrap",
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(4)
  },
  asideWrapper: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flex: 1
  },
  error: {
    marginBottom: theme.spacing(2)
  },
  buttonWrapper: {
    display: "flex",
    "&>button": {
      marginRight: "1.2em"
    }
  },
  fields: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  row: {
    display: "flex",
    "& > div": {
      flex: 1
    },
    "& > :first-child": {
      marginRight: 10
    }
  }
}));

const MyPremise: React.FC<Props> = props => {
  const classes = useStyles();
  const premiseId = _.get(props, "match.params.id");
  const { showMessage } = useContext(UIMessageContext);

  useEffect(() => {
    if (premiseId) {
      props.getPremise(premiseId);
    }
  }, [premiseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const { premise } = props;

  if (!premiseId || !premise) return null;

  return (
    <PageRoot>
      <PageHeader
        label={routes.MY_PREMISES.label}
        breadcrumbLinks={[
          {
            label: "Premises Information",
            route: routes.MY_PREMISES.url
          },
          {
            active: true,
            label: _.get(premise, "premisesName", "")
          }
        ]}
      />

      <Card className={classes.formCard}>
        <div className={classes.header}>
          <Icon className={classes.icon} fontSize="large" name="person" />
          <Typography color="textSecondary" component="h2" variant="h5">
            {_.get(premise, "premisesName", "")}
          </Typography>
        </div>
        <RxForm
          className={classes.form}
          confirmMessage={PREMISES_AUTHORISATION_CONFIRMATION}
          onSubmit={value => {
            const premise: Premises = {
              ...props.premise,
              addressDetails: {
                ...props.premise.addressDetails,
                ...value.postalAddress,
                postalAndStreetAddessSame: value.postalAndStreetAddessSame
              },
              phoneNumber: value.phoneNumber,
              faxNumber: value.faxNumber || null,
              emailAddress: value.emailAddress,
              website: value.website || null
            };
            return props.updatePremise(premise, () => {
              showMessage("Update successful", "success");
            });
          }}
        >
          <OnUnSaveChange />
          <div className={classes.contentWrapper}>
            <div className={classes.fields}>
              <TextField
                id="siteDescription"
                label="Site Description"
                disabled
                value={_.get(premise, "siteDescription") || ""}
              />

              <FormTextField
                required
                id="phoneNumber"
                name="phoneNumber"
                label="Phone Number"
                defaultValue={premise.phoneNumber}
              />
              <FormTextField
                id="faxNumber"
                name="faxNumber"
                label="Fax Number"
                defaultValue={premise.faxNumber}
              />
              <FormTextField
                required
                id="emailAddress"
                name="emailAddress"
                label="Email"
                inputProps={emailValidator}
                defaultValue={premise.emailAddress}
              />
              <FormTextField
                label="Website"
                id="website"
                name="website"
                inputProps={websiteValidator}
                defaultValue={premise.website}
              />

              <TextField
                id="streetAddressOtherDetails"
                label="Street Address Other detail"
                disabled
                value={
                  _.get(premise, "addressDetails.streetAddressOtherDetails") ||
                  ""
                }
              />

              <TextField
                disabled
                id="streetAddress"
                label="Street Address"
                value={_.get(premise, "addressDetails.streetAddress") || ""}
              />
              <div className={classes.row}>
                <TextField
                  id="streetAddressSuburbOrRDNumber"
                  disabled
                  label="Suburb"
                  value={
                    _.get(
                      premise,
                      "addressDetails.streetAddressSuburbOrRDNumber"
                    ) || ""
                  }
                />

                <TextField
                  id="streetAddressTownOrCity"
                  disabled
                  label="City"
                  value={
                    _.get(premise, "addressDetails.streetAddressTownOrCity") ||
                    ""
                  }
                />
              </div>
              <div className={classes.row}>
                <TextField
                  id="streetAddressCountry"
                  disabled
                  label="Country"
                  value={
                    _.get(premise, "addressDetails.streetAddressCountry") || ""
                  }
                />

                <TextField
                  disabled
                  id="streetAddressPostCode"
                  label="Post Code"
                  value={
                    _.get(premise, "addressDetails.streetAddressPostCode") || ""
                  }
                />
              </div>

              <CheckboxField
                name="postalAndStreetAddessSame"
                label="Postal address is the same as street address"
                defaultValue={_.get(
                  premise,
                  "addressDetails.postalAndStreetAddessSame"
                )}
              />
              <FormValues>
                {({ formValues }) => {
                  const { postalAndStreetAddessSame } = formValues;

                  if (postalAndStreetAddessSame) return null;

                  return (
                    <FormSection name="postalAddress">
                      <FormTextField
                        id="postalAddressOtherDetails"
                        name="postalAddressOtherDetails"
                        label="Postal Address Other detail"
                        destroyValueOnUnmount
                        defaultValue={_.get(
                          premise,
                          "addressDetails.postalAddressOtherDetails"
                        )}
                      />

                      <FormTextField
                        required
                        destroyValueOnUnmount
                        id="postalAddress"
                        name="postalAddress"
                        label="Postal Address"
                        defaultValue={_.get(
                          premise,
                          "addressDetails.postalAddress"
                        )}
                      />
                      <div className={classes.row}>
                        <FormTextField
                          destroyValueOnUnmount
                          id="postalAddressSuburbOrRDNumber"
                          name="postalAddressSuburbOrRDNumber"
                          label="Suburb"
                          defaultValue={_.get(
                            premise,
                            "addressDetails.postalAddressSuburbOrRDNumber"
                          )}
                        />
                        <FormTextField
                          required
                          destroyValueOnUnmount
                          id="postalAddressTownOrCity"
                          name="postalAddressTownOrCity"
                          label="City"
                          defaultValue={_.get(
                            premise,
                            "addressDetails.postalAddressTownOrCity"
                          )}
                        />
                      </div>
                      <div className={classes.row}>
                        <FormTextField
                          destroyValueOnUnmount
                          id="postalAddressCountry"
                          name="postalAddressCountry"
                          label="Country"
                          defaultValue={_.get(
                            premise,
                            "addressDetails.postalAddressCountry"
                          )}
                        />
                        <FormTextField
                          required
                          destroyValueOnUnmount
                          id="postalAddressPostCode"
                          name="postalAddressPostCode"
                          label="Post Code"
                          defaultValue={_.get(
                            premise,
                            "addressDetails.postalAddressPostCode"
                          )}
                        />
                      </div>
                    </FormSection>
                  );
                }}
              </FormValues>
            </div>

            <aside className={classes.asideWrapper}>
              {premise.parentLegalEntity && (
                <DetailCard
                  id={premise.parentLegalEntity.cmAcmCaseIdentifier}
                  title={premise.parentLegalEntity.legalEntityName}
                  onView={() =>
                    props.history.push(
                      `${routes.MY_LEGAL_ENTITY.url}${premise.parentLegalEntity.cmAcmCaseIdentifier}`
                    )
                  }
                  listItems={[
                    {
                      icon: "business",
                      label: `${premise.parentLegalEntity.premisesCount} premises`
                    }
                  ]}
                  btnDisabled={
                    props.loading ||
                    (!props.loading && !premise.parentLegalEntity.viewable)
                  }
                />
              )}
            </aside>
          </div>

          {props.error && (
            <ErrorMessage className={classes.error} error={props.error} />
          )}
          <div className={classes.buttonWrapper}>
            <SubmitButton label="Update" />
            <ResetButton label="Reset" />
            <Button
              onClick={() => {
                props.history.goBack();
              }}
              id="close"
            >
              Close
            </Button>
          </div>
        </RxForm>
      </Card>
    </PageRoot>
  );
};

const mapDispatchToProps = {
  getPremise: premisesActions.getPremise,
  updatePremise: premisesActions.updatePremise
};

const mapStateToProps = ({ premises }: any, ownProps: Props) => {
  const {
    params: { id }
  }: any = ownProps.match;
  const currentPremise: Premises = premises.premises.find(
    (premise: Premises) => premise.cmAcmCaseIdentifier === id
  );
  return {
    error: premises.error,
    premise: currentPremise,
    loading: premises.loading
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MyPremise));
